import type { IConfigurationEntity, ISetting, SettingsData, SettingValueType } from "../../interfaces";
import { EAppId } from "../../interfaces";
import { isURLValid } from "../SettingsHelper/SettingsHelper";
import { DMSNextDefaultSettings, DMSNextSetting, EDMSNextSettingKey, fileOpenPreference } from "./DMSNext/DMSNext";
import { emailItemsSelectionOptions, MailDefaultSettings, MailSetting, EMailSettingKey } from "./Mail/Mail";

// Mapping of AppId to its respective settings
const settingsMap: Record<EAppId, { settings: Record<string, IConfigurationEntity>, defaults: SettingsData }> = {
    [EAppId.Mail]: { settings: MailSetting, defaults: MailDefaultSettings },
    [EAppId.DMSNext]: { settings: DMSNextSetting, defaults: DMSNextDefaultSettings }
};

export const appSettingInfoLink: Record<EAppId, string> = {
    [EAppId.Mail]: "https://support.macroview365.com/hc/en-us/articles/360001251215-MacroView-Mail-Settings",
    [EAppId.DMSNext]: "https://support.macroview365.com/hc/en-us/articles/13243474020367-DMS-Next-Settings",
};

function getSettingsCollection(applicationId: EAppId) {
    return settingsMap[applicationId]?.settings;
}

function getDefaultSettingsCollection(applicationId: EAppId) {
    return settingsMap[applicationId]?.defaults ?? {};
}

function getDisplayValueForNumberSetting(key: string, value: number): string {
    switch (key) {
        case EMailSettingKey.defaultItemSelection: {
            const option = emailItemsSelectionOptions.find(item => item.key === value);
            return option ? option.text : String(value);
        }

        case EDMSNextSettingKey.fileOpenPreference: {
            const option = fileOpenPreference.find(item => item.key === value);
            return option ? option.text : String(value);
        }

        default:
            return String(value);
    }
}

export function getSettingDisplayName(applicationId: EAppId, key: string): string {
    return getSettingsCollection(applicationId)?.[key]?.name ?? key;
}

export function getAvailableConfigurationEntities(applicationId?: EAppId, configurationKeysToIgnore?: string[]) {
    const settingsCollection = applicationId ? getSettingsCollection(applicationId) : undefined;
    if (!settingsCollection) {
        return [];
    }

    return Object.values(settingsCollection).filter(x => !configurationKeysToIgnore?.includes(x.key));
}

export function getDefaultSettings(applicationId: EAppId): SettingsData {
    return getDefaultSettingsCollection(applicationId);
}

export function isValidKey(applicationId: EAppId, key: string): boolean {
    return key in (getSettingsCollection(applicationId) ?? {});
}

export function getSettingArrayFromSettingsData(applicationId?: EAppId, data?: SettingsData): ISetting[] {
    if (!data) {
        return [];
    }

    return Object.entries(data).map(([key, value]) => ({
        key,
        name: applicationId ? getSettingDisplayName(applicationId, key) : key,
        value
    }));
}

export function isValidValue(applicationId: EAppId, key: string, value: SettingValueType): boolean {
    if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') {
        return false;
    }

    const settingsCollection = getSettingsCollection(applicationId);
    if (!settingsCollection) {
        return false;
    }

    const setting = settingsCollection[key];
    if (!setting) {
        return false;
    }

    // Validate value type
    switch (setting.type) {
        case "boolean":
            return typeof value === "boolean";

        case "string":
            return typeof value === "string";

        case "number":
            return typeof value === "number";

        case "URL":
            return isURLValid(value as string);

        default:
            return false;
    }
}

export function getDisplayValueForSetting(item: ISetting): string {
    if (typeof item.value === 'number') {
        return getDisplayValueForNumberSetting(item.key, item.value);
    }

    return item.value.toString();
}
